import {
  Calculation,
  CalculationType,
  CalculationValue,
  HistoricalUtrs,
  ValueRole,
} from '@g17eco/types/insight-custom-dashboard';
import { createExpression, FormulaVariables, tryCalculation } from '@utils/formula';
import {
  getActualUtrvsGroupByDate,
  getLatestActualUtrvs,
  getSumOfVariables,
  getVariablesWithValues,
  isVariableCurrencyType,
} from '@routes/custom-dashboard/items/charts/multi-utrs-chart/utils';
import { UtrVariables } from '@routes/summary/insights/utils/constants';
import { CHART_DECIMAL_PLACES, CHART_COLORS } from '@routes/custom-dashboard/utils';
import { getDecimalAsNumber } from '@utils/number';
import { isNumericString, truncate } from '@utils/string';
import { ChartDataProps, ChartDataResponse } from '@routes/custom-dashboard/items/types';
import { UtrvInitiativeRef } from '@g17eco/types/universalTrackerValue';
import { collectUtrvInitiatives, sliceUtrsDataByInitiative } from '../tables/utils';

// Google Charts can't infer the type of an all-null column and throws even when
// sibling columns have numbers — strip those series (and their headers) first.
const dropAllNullSeries = (
  headerLabels: string[],
  rows: (string | number | null)[][],
): { headerLabels: string[]; rows: (string | number | null)[][] } => {
  const keptColumnIndices = headerLabels
    .map((_, columnIndex) => columnIndex)
    .filter((columnIndex) =>
      rows.some((row) => {
        const cell = row[columnIndex + 1];
        return cell !== null && cell !== undefined;
      }),
    );

  return {
    headerLabels: keptColumnIndices.map((columnIndex) => headerLabels[columnIndex]),
    rows: rows.map((row) => [row[0], ...keptColumnIndices.map((columnIndex) => row[columnIndex + 1])]),
  };
};

type SeriesDef = {
  label: string;
  calcValue: CalculationValue | undefined;
  initiative: UtrvInitiativeRef;
};

const valueLabel = (calcValue: CalculationValue | undefined, fallback: string) =>
  calcValue?.role || calcValue?.name || fallback;

// Formula/Percentage emit one series per (calculation value, initiative) pair. Sum collapses to
// one series per initiative — the cross-variable aggregation is done later in evaluateCalculation.
// Label format: in disaggregated mode the initiative is the primary dimension, so it leads the
// label. The calc name is only appended when multiple calc values would otherwise collide.
const buildSeriesDefs = (calculation: Calculation, initiatives: UtrvInitiativeRef[]): SeriesDef[] => {
  if (calculation.type === CalculationType.Sum) {
    return initiatives.map((initiative) => ({
      label: initiative.name,
      calcValue: calculation.values[0],
      initiative,
    }));
  }
  const multipleValues = calculation.values.length > 1;
  return calculation.values.flatMap((calcValue) =>
    initiatives.map((initiative) => ({
      label: multipleValues
        ? `${initiative.name} — ${valueLabel(calcValue, 'Value')}`
        : initiative.name,
      calcValue,
      initiative,
    }))
  );
};

const getVariablesWithValuesForInitiative = ({
  utrvs,
  initiative,
  variables,
  utrsData,
  fallback,
}: {
  utrvs: HistoricalUtrs['utrvs'];
  initiative: UtrvInitiativeRef;
  variables: UtrVariables;
  utrsData: HistoricalUtrs[];
  fallback?: string | number;
}) => {
  return getVariablesWithValues({
    utrsData: sliceUtrsDataByInitiative(utrsData, initiative._id),
    variables,
    utrvs: utrvs.filter((utrv) => utrv.initiative?._id === initiative._id),
    fallback,
  });
};

const percentageOf = (value: number, sum: number) => (sum === 0 ? 0 : (100 * value) / sum);

// Stages is produced by server-side `isDefault` dashboards only and never co-occurs with
// customer subsidiary filters, so disaggregated charts return empty rather than fall through.
const isStagesOnly = (calculation: Calculation) => calculation.type === CalculationType.Stages;

const evaluateCalculation = ({
  calculation,
  calcValue,
  variablesForInitiative,
  fallback,
  infinityFallback,
}: {
  calculation: Calculation;
  calcValue: CalculationValue | undefined;
  variablesForInitiative: ReturnType<typeof getVariablesWithValues>;
  fallback: number;
  infinityFallback?: number | null;
}): number | null => {
  switch (calculation.type) {
    case CalculationType.Sum:
      return getSumOfVariables(variablesForInitiative);
    case CalculationType.Percentage: {
      const sum = getSumOfVariables(variablesForInitiative);
      const numerator = tryCalculation({
        formula: calcValue?.formula ?? '',
        variables: variablesForInitiative,
        fallback,
        infinityFallback,
      });
      return percentageOf(Number(numerator), sum);
    }
    case CalculationType.Formula:
    default: {
      const result = tryCalculation({
        formula: calcValue?.formula ?? '',
        variables: variablesForInitiative,
        fallback,
        infinityFallback,
      });
      return typeof result === 'string' ? Number(result) : result;
    }
  }
};

export const getColumnChartData = async ({ utrsData, variables, calculation }: ChartDataProps<HistoricalUtrs>) => {
  if (isStagesOnly(calculation)) return { chartData: [] };

  const fallback = 0;
  const utrvsGroupByDate = getActualUtrvsGroupByDate(utrsData) as { [key: string]: HistoricalUtrs['utrvs'] };
  const initiatives = collectUtrvInitiatives(Object.values(utrvsGroupByDate).flat());
  const seriesDefs = buildSeriesDefs(calculation, initiatives);

  const rows: (string | number | { role: ValueRole })[][] = Object.keys(utrvsGroupByDate).map((date) => {
    const utrvsForDate = utrvsGroupByDate[date];
    const cells = seriesDefs.map(({ calcValue, initiative }) => {
      const variablesForInitiative = getVariablesWithValuesForInitiative({
        utrvs: utrvsForDate,
        initiative,
        variables,
        utrsData,
        fallback: '',
      });
      return evaluateCalculation({ calculation, calcValue, variablesForInitiative, fallback }) ?? fallback;
    });
    return [date, ...cells];
  });

  return {
    chartData: [['Time', ...seriesDefs.map(({ label }) => label)], ...rows],
  };
};

export const getPieChartData = async ({
  utrsData,
  variables,
  calculation,
}: ChartDataProps<HistoricalUtrs>): Promise<ChartDataResponse> => {
  if (isStagesOnly(calculation)) return { chartData: [] };

  const { latestActualUtrvs = [], effectiveDate, period, aggregated } = getLatestActualUtrvs(utrsData);

  const initiatives = collectUtrvInitiatives(latestActualUtrvs);
  const seriesDefs = buildSeriesDefs(calculation, initiatives);

  const data = seriesDefs.map(({ label, calcValue, initiative }) => {
    const variablesForInitiative = getVariablesWithValuesForInitiative({
      utrvs: latestActualUtrvs,
      initiative,
      variables,
      utrsData,
      fallback: '',
    });
    return [label, evaluateCalculation({ calculation, calcValue, variablesForInitiative, fallback: 0 }) ?? 0];
  });

  return { chartData: [['Key', 'Value'], ...data], effectiveDate, period, aggregated };
};

export const getLineChartData = async ({ utrsData, variables, calculation }: ChartDataProps<HistoricalUtrs>) => {
  if (isStagesOnly(calculation)) return { chartData: [] };

  const utrvsGroupByDate = getActualUtrvsGroupByDate(utrsData) as { [key: string]: HistoricalUtrs['utrvs'] };
  const initiatives = collectUtrvInitiatives(Object.values(utrvsGroupByDate).flat());
  const seriesDefs = buildSeriesDefs(calculation, initiatives);
  const fallback = 0;

  const rows: (string | number | null)[][] = Object.keys(utrvsGroupByDate).map((date) => {
    const utrvsForDate = utrvsGroupByDate[date];
    const cells = seriesDefs.map(({ calcValue, initiative }) => {
      const variablesForInitiative = getVariablesWithValuesForInitiative({
        utrvs: utrvsForDate,
        initiative,
        variables,
        utrsData,
        fallback: '',
      });
      return evaluateCalculation({
        calculation,
        calcValue,
        variablesForInitiative,
        fallback,
        infinityFallback: null,
      });
    });
    return [date, ...cells];
  });

  const { headerLabels: keptLabels, rows: filteredRows } = dropAllNullSeries(
    seriesDefs.map(({ label }) => label),
    rows,
  );

  if (keptLabels.length === 0) {
    return { chartData: [] };
  }

  return {
    chartData: [['Time', ...keptLabels], ...filteredRows],
  };
};

// Bar annotations render inside each bar in a horizontal layout. Truncate the subsidiary
// portion so long names stay on one line instead of wrapping into the next bar.
const BAR_LABEL_MAX_LENGTH = 14;

const buildBarAnnotation = ({
  calculation,
  calcValue,
  label,
  value,
  variablesWithFormattedValues,
}: {
  calculation: Calculation;
  calcValue: CalculationValue | undefined;
  label: string;
  value: number;
  variablesWithFormattedValues: FormulaVariables;
}): string => {
  const formatted = getDecimalAsNumber(String(value), CHART_DECIMAL_PLACES);
  const shortLabel = truncate(label, BAR_LABEL_MAX_LENGTH);
  switch (calculation.type) {
    case CalculationType.Sum:
      return `${shortLabel} | sum = ${formatted}`;
    case CalculationType.Percentage:
      return `${shortLabel} | ${formatted}%`;
    default:
      return createExpression(`${shortLabel} | ${calcValue?.formula ?? ''}`, variablesWithFormattedValues, 0);
  }
};

export const getBarChartData = async ({
  utrsData,
  variables,
  calculation,
  currencyConfig,
}: ChartDataProps<HistoricalUtrs>) => {
  const { headers = [] } = calculation;

  const chartDataHeaders = headers.map((header) => {
    if ('name' in header) {
      return header.name;
    }
    if ('role' in header) {
      return { role: header.role };
    }
    return '';
  });

  if (isStagesOnly(calculation)) return { chartData: [] };

  const { latestActualUtrvs = [], effectiveDate, period, aggregated } = getLatestActualUtrvs(utrsData);

  const initiatives = collectUtrvInitiatives(latestActualUtrvs);
  const seriesDefs = buildSeriesDefs(calculation, initiatives);

  const data = seriesDefs.map(({ label, calcValue, initiative }, index) => {
    const variablesForInitiative = getVariablesWithValuesForInitiative({
      utrvs: latestActualUtrvs,
      initiative,
      variables,
      utrsData,
      fallback: '',
    });
    const currencySymbol = currencyConfig?.currencySymbol ?? '';
    const variablesWithFormattedValues = Object.entries(variablesForInitiative).reduce<FormulaVariables>(
      (acc, [key, value]) => {
        if (isNumericString(value)) {
          const formattedNumber = getDecimalAsNumber(value, CHART_DECIMAL_PLACES);
          const variable = variables[key as keyof typeof variables];
          const showCurrency = variable && isVariableCurrencyType(variable, utrsData);
          acc[key] = showCurrency && currencySymbol ? `${currencySymbol}${formattedNumber}` : formattedNumber;
        } else {
          acc[key] = value;
        }
        return acc;
      },
      {}
    );

    const value = evaluateCalculation({ calculation, calcValue, variablesForInitiative, fallback: 0 }) ?? 0;
    const annotation = buildBarAnnotation({
      calculation,
      calcValue,
      label,
      value,
      variablesWithFormattedValues,
    });

    return [label, value, annotation, CHART_COLORS[index] ?? ''];
  });

  return { chartData: [chartDataHeaders, ...data], effectiveDate, period, aggregated };
};
